// Hook personalizado para gestionar ventas
// Proporciona funciones para obtener, crear, actualizar y eliminar ventas

import { useState, useEffect, useCallback, useRef } from 'react'
import {
  getAllSales,
  getSale,
  getSaleByOrderId,
  createSale,
  updateSale,
  deleteSale
} from '../services/sales.service'

/**
 * Hook para obtener todas las ventas
 * @param {Object} filters - Filtros opcionales
 * @returns {Object} { sales, loading, error, refetch }
 */
export const useSales = (filters = {}) => {
  const [sales, setSales] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const filtersKey = JSON.stringify(filters || {})
  const filtersRef = useRef({ key: filtersKey, value: filters || {} })
  if (filtersRef.current.key !== filtersKey) {
    filtersRef.current = { key: filtersKey, value: filters || {} }
  }
  const normalizedFilters = filtersRef.current.value

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getAllSales(normalizedFilters)
      setSales(data)
    } catch (err) {
      console.error('Error obteniendo ventas:', err)
      setError(err.message || 'Error al obtener ventas')
    } finally {
      setLoading(false)
    }
  }, [normalizedFilters])

  useEffect(() => {
    fetchSales()
  }, [fetchSales])

  return {
    sales,
    loading,
    error,
    refetch: fetchSales
  }
}

/**
 * Hook para obtener una venta específica
 * @param {string} saleId - ID de la venta
 * @returns {Object} { sale, loading, error, refetch }
 */
export const useSale = (saleId) => {
  const [sale, setSale] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSale = useCallback(async () => {
    if (!saleId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await getSale(saleId)
      setSale(data)
    } catch (err) {
      console.error('Error obteniendo venta:', err)
      setError(err.message || 'Error al obtener la venta')
    } finally {
      setLoading(false)
    }
  }, [saleId])

  useEffect(() => {
    fetchSale()
  }, [fetchSale])

  return {
    sale,
    loading,
    error,
    refetch: fetchSale
  }
}

/**
 * Hook para obtener venta por orderId
 * @param {string} orderId - ID del pedido
 * @returns {Object} { sale, loading, error, refetch }
 */
export const useSaleByOrderId = (orderId) => {
  const [sale, setSale] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchSale = useCallback(async () => {
    if (!orderId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await getSaleByOrderId(orderId)
      setSale(data)
    } catch (err) {
      console.error('Error obteniendo venta por orderId:', err)
      setError(err.message || 'Error al obtener la venta')
    } finally {
      setLoading(false)
    }
  }, [orderId])

  useEffect(() => {
    fetchSale()
  }, [fetchSale])

  return {
    sale,
    loading,
    error,
    refetch: fetchSale
  }
}

/**
 * Hook para operaciones de ventas
 * @returns {Object} { create, update, delete, loading }
 */
export const useSaleOperations = () => {
  const [loading, setLoading] = useState(false)

  const create = async (saleData) => {
    try {
      setLoading(true)
      return await createSale(saleData)
    } catch (error) {
      console.error('Error creando venta:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const update = async (saleId, updates) => {
    try {
      setLoading(true)
      await updateSale(saleId, updates)
    } catch (error) {
      console.error('Error actualizando venta:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  const remove = async (saleId) => {
    try {
      setLoading(true)
      await deleteSale(saleId)
    } catch (error) {
      console.error('Error eliminando venta:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }

  return {
    create,
    update,
    delete: remove,
    loading
  }
}

